// מסמך ה-iframe של בלוק app (11, 2026-10-13) — טהור כמעט (readTheme קורא DOM), בלי JSX: נבדק ב-vitest.
// eslint-disable-next-line @typescript-eslint/triple-slash-reference -- ?raw של vite; ה-tsconfig הראשי לא כולל את assets.d.ts
/// <reference path="./assets.d.ts" />
import type { AppBlock as AppBlockT } from '@aibify/core';
import runtime from './app-runtime.js?raw';
import kitSeries from './kit/series.js?raw';
import kitExplorer from './kit/explorer.js?raw';
import kitControls from './kit/controls.js?raw';
import kitTable from './kit/table.js?raw';
import kitCss from './kit/kit.css?raw';

/**
 * ערכת התצוגה (kit/*.js) כסקריפט אחד ל-iframe: שורות import (רק ./series.js) נמחקות, מילת הייצוא בתחילת שורה מוסרת,
 * והכול רץ בפונקציה אחת — כך explorer/controls רואים את series בלי מודולים. window.__abKit = כל מה שיוצא.
 */
export function kitScript(files: string[] = [kitSeries, kitExplorer, kitControls, kitTable]): string {
  const names: string[] = [];
  const body = files
    .map((src) =>
      src
        .replace(/^import\s[^;]*from\s+'\.\/series\.js';[ \t]*$/gm, '')
        .replace(/^export (function|const) ([A-Za-z_$][\w$]*)/gm, (_m, kw: string, name: string) => {
          names.push(name);
          return `${kw} ${name}`;
        }),
    )
    .join('\n');
  return `(function(){'use strict';\n${body}\nwindow.__abKit={${names.join(',')}};})();`;
}
const KIT = kitScript();

export const APP_SANDBOX = 'allow-scripts allow-downloads';

/**
 * קישור מתוך בלוק app (באג c95e2dad, 2026-10-20): ה-sandbox חוסם ניווט של הדף העליון, חלונות קופצים ופרוטוקולים חיצוניים —
 * לכן <a href> שנלחץ ב-iframe (או ab.openLink) עובר כבקשה למארח, והמארח פותח רק מה שברשימה: http(s) בלשונית חדשה
 * (noopener), ופרוטוקולים של תוכנות עבודה מוכרות (mailto, tel, priority — מסך בפריוריטי למחשב) בדף עצמו — הדפדפן
 * מעביר אותם לתוכנה והדף נשאר. כל פרוטוקול אחר (javascript:, data:, file:, ms-*, …) נדחה: קוד באפליקציה לא מפעיל תוכנה
 * מקומית שרירותית.
 */
export const APP_LINK_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:', 'priority:'] as const;

export type AppLinkTarget = { url: string; mode: 'tab' | 'self' };

export function appLinkTarget(href: unknown): AppLinkTarget | null {
  if (typeof href !== 'string') return null;
  const s = href.trim();
  if (!s || s.length > 2000) return null;
  // eslint-disable-next-line no-control-regex -- תווי בקרה בכתובת
  if (/[\u0000-\u001f\u007f]/.test(s)) return null;
  const m = /^([A-Za-z][A-Za-z0-9+.-]*):/.exec(s);
  if (!m) return null;
  const proto = `${m[1]!.toLowerCase()}:`;
  if (!(APP_LINK_PROTOCOLS as readonly string[]).includes(proto)) return null;
  if (proto === 'http:' || proto === 'https:') {
    try {
      const u = new URL(s);
      if (!u.hostname) return null;
      return { url: u.href, mode: 'tab' };
    } catch {
      return null;
    }
  }
  return { url: s, mode: 'self' };
}

export function appCsp(origin: string): string {
  const o = origin && origin !== 'null' ? ` ${origin}` : '';
  return [
    "default-src 'none'",
    `script-src 'unsafe-inline' 'unsafe-eval' https:${o}`,
    `style-src 'unsafe-inline' https:${o}`,
    `font-src data: https:${o}`,
    'img-src data: blob: https:',
    'media-src data: blob: https:',
    'worker-src blob:',
    "connect-src 'none'",
    "form-action 'none'",
    "base-uri 'none'",
  ].join('; ');
}

/** tokens של העיצוב שעוברים ל-iframe (ערכי HSL גולמיים כמו בדף המארח). */
export const THEME_VARS = [
  '--background', '--foreground', '--card', '--muted', '--muted-foreground', '--border', '--input', '--primary',
  '--primary-foreground', '--ring', '--action', '--action-foreground', '--state-ok', '--state-error', '--state-degraded',
  '--chart-1', '--chart-2', '--chart-3', '--chart-4', '--chart-5', '--chart-6', '--radius', '--primary-soft', '--mint', '--laser',
  // 23 §1.1: ערכת app@1 (Tailwind @theme) — משטח מורם, hover של כפתור ראשי, רדיוס קטן, צל
  '--raised', '--action-hover', '--radius-sm', '--shadow-color',
];

export type AppTheme = { vars: Record<string, string>; dark: boolean };


/** JSON בתוך <script>: "<" מוברח כדי ש-</script> בנתונים לא יסגור את התג. */
const safeJson = (v: unknown) => JSON.stringify(v ?? null).replace(/</g, '\\u003c').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
const cssVal = (s: string) => s.replace(/[;{}<>]/g, '');

const BASE_CSS = `html,body{margin:0;background:transparent;color:hsl(var(--foreground));font-family:Figtree,"Noto Sans Hebrew",system-ui,sans-serif;font-size:14px;line-height:1.5}
body{padding:2px;display:flow-root}*{box-sizing:border-box}
input,select,button,textarea{font:inherit;color:inherit}
.ab-row{display:flex;gap:16px;flex-wrap:wrap}.ab-col{flex:1 1 320px;min-inline-size:0}
.ab-muted{color:hsl(var(--muted-foreground))}
.ab-table-wrap{overflow:auto;border:1px solid hsl(var(--border));border-radius:8px}
.ab-table{border-collapse:collapse;inline-size:100%;font-variant-numeric:tabular-nums}
.ab-table th{position:sticky;inset-block-start:0;background:hsl(var(--card));color:hsl(var(--muted-foreground));font-weight:500;text-align:start;padding:8px 12px;border-block-end:1px solid hsl(var(--border));cursor:pointer;white-space:nowrap;user-select:none}
.ab-table td{padding:8px 12px;border-block-end:1px solid hsl(var(--border) / .6)}
.ab-table .ab-num{text-align:end}
.ab-table tbody tr:hover td,.ab-table tr.ab-hl td{background:hsl(var(--primary) / .08)}
.ab-table tr.ab-off td{opacity:.45}
.ab-table td.ab-has-bar{position:relative}.ab-cell-bar{position:absolute;inset-inline-end:12px;inset-block-end:4px;max-inline-size:calc(100% - 24px);block-size:3px;border-radius:2px;background:hsl(var(--primary) / .55)}
.ab-table tfoot td{font-weight:600;background:hsl(var(--muted) / .6);border-block-start:1px solid hsl(var(--border));border-block-end:0}
.ab-swatch{display:inline-block;inline-size:10px;block-size:10px;border-radius:3px;margin-inline-end:8px;vertical-align:middle}
.ab-btn{background:hsl(var(--card));border:1px solid hsl(var(--border));border-radius:4px;padding:5px 12px;cursor:pointer}
.ab-btn:hover{border-color:hsl(var(--primary))}.ab-btn[aria-pressed=true]{background:hsl(var(--primary));color:hsl(var(--primary-foreground));border-color:transparent}
.ab-input,.ab-select{background:hsl(var(--background));border:1px solid hsl(var(--border));border-radius:4px;padding:6px 10px}
.ab-error{background:hsl(var(--state-error) / .12);color:hsl(var(--state-error));border:1px solid hsl(var(--state-error) / .4);border-radius:8px;padding:8px 12px;margin-block-end:8px;white-space:pre-wrap}
.ab-freshness{display:flex;flex-wrap:wrap;align-items:center;gap:4px 12px;font-size:13px;color:hsl(var(--muted-foreground));min-block-size:32px}
.ab-freshness span:empty{display:none}.ab-freshness [role=status]{color:hsl(var(--foreground));font-weight:500}
.ab-freshness button{margin-inline-start:auto;background:hsl(var(--card));color:hsl(var(--foreground));border:1px solid hsl(var(--border));border-radius:4px;padding:4px 12px;cursor:pointer;font:inherit}
.ab-freshness button:disabled{opacity:.5;cursor:default}`;

/** ה-srcdoc של בלוק app — טהור (חוץ מ-new URL), נבדק ב-vitest. fonts — תיקיית הגופנים (23 §5: /vendor/fonts/; '' = בלי). */
export type AppAssets = { echarts: string; react: string; reactDom: string; fonts?: string };
/** מוגשים ע"י האתר ב-/vendor/ (packages/display/vendor.ts). */
const ASSETS: AppAssets = { echarts: '/vendor/echarts.min.js', react: '/vendor/react.production.min.js', reactDom: '/vendor/react-dom.production.min.js', fonts: '/vendor/fonts/' };

/** גופני המותג (23 §5; web-visual-identity: Figtree + Noto Sans Hebrew): אותם קבצים ו-unicode-range כמו apps/web/app/index.css,
 *  מ-/vendor/fonts/ — גם לדוחות. השמות הישנים (Inter/Heebo) נשארים ככינוי לאותם קבצים: תבניות app שנבנו עליהם מקבלות את הגופן החדש. */
export const FONT_FILES = ['noto-sans-hebrew-hebrew-wght-normal.woff2', 'figtree-latin-wght-normal.woff2', 'figtree-latin-ext-wght-normal.woff2'] as const;
const LATIN_EXT = 'U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF';
const HEBREW = 'U+0307-0308, U+0590-05FF, U+200C-2010, U+20AA, U+25CC, U+FB1D-FB4F';
const LATIN = 'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD';
export function fontCss(url: (file: string) => string): string {
  const face = (family: string, file: string, range: string) =>
    `@font-face{font-family:"${family}";font-style:normal;font-display:swap;font-weight:100 900;src:url("${url(file)}") format("woff2-variations");unicode-range:${range}}`;
  return [
    face('Noto Sans Hebrew', FONT_FILES[0], HEBREW),
    face('Figtree', FONT_FILES[1], LATIN),
    face('Figtree', FONT_FILES[2], LATIN_EXT),
    face('Heebo', FONT_FILES[0], HEBREW),
    face('Heebo', FONT_FILES[1], LATIN),
    face('Inter', FONT_FILES[1], LATIN),
  ].join('');
}

/** canCall (16): יש לבלוק ריצה (source.runId) וגשר בדף — אחרת ab.canCall=false והקוד מסתיר כפתורים. */
export function appSrcDoc(block: Pick<AppBlockT, 'id' | 'html' | 'js' | 'data'>, theme: AppTheme, origin: string, assets: AppAssets = ASSETS, o: { canCall?: boolean } = {}): string {
  const root = Object.entries(theme.vars).map(([k, v]) => `${k}:${cssVal(v)}`).join(';');
  const abs = (u: string) => new URL(u, origin && origin !== 'null' ? origin : 'http://localhost').href;
  const react = typeof block.js === 'string' && block.js.length > 0;
  const esc = (s: string) => s.replace(/<\/script/gi, '<\\/script');
  return (
    '<!doctype html><html dir="rtl" lang="he"><head><meta charset="utf-8">' +
    `<meta http-equiv="Content-Security-Policy" content="${appCsp(origin)}">` +
    '<meta name="viewport" content="width=device-width,initial-scale=1">' +
    `<style>${assets.fonts === '' ? '' : fontCss((f) => abs(`${assets.fonts ?? ASSETS.fonts}${f}`))}:root{${root};color-scheme:${theme.dark ? 'dark' : 'light'}}${BASE_CSS}\n${kitCss}</style>` +
    `<script type="application/json" id="ab-config">${safeJson({ id: block.id, vars: theme.vars, dark: theme.dark, canCall: !!o.canCall })}</script>` +
    (block.data === undefined ? '' : `<script type="application/json" id="ab-data">${safeJson(block.data)}</script>`) +
    `<script src="${abs(assets.echarts)}"></script>` +
    (react ? `<script src="${abs(assets.react)}"></script><script src="${abs(assets.reactDom)}"></script>` : '') +
    `<script>${esc(KIT)}</script>` +
    `<script>${esc(runtime)}</script>` +
    `</head><body>${block.html ?? ''}` +
    (react ? `<div id="root"></div><script>${esc(block.js!)}\n;ab.mount(typeof __abApp === 'undefined' ? null : __abApp);</script>` : '') +
    '</body></html>'
  );
}

